import { useQuery } from '@apollo/client';
import {
  ChallengeApplicationStats,
  GET_CHALLENGE_APPLICATION_STATS,
} from '@frontend/data-access';
import { Input, Select, Table } from 'antd';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import React, { useState } from 'react';

const stateOptions = [
  {
    value: null,
    label: 'All',
  },
  {
    value: 'ACTIVE',
    label: 'Registration open',
  },
  {
    value: 'ENDED',
    label: 'Registration closed',
  },
];

function IntraChallengeIdeasStats({ className }: { className?: string }) {
  const { t } = useTranslation('admin');
  const { loading, data } = useQuery<{
    getApplicationByRegistration: ChallengeApplicationStats[];
  }>(GET_CHALLENGE_APPLICATION_STATS, {
    fetchPolicy: 'no-cache',
    variables: {
      type: 'INTERNAL',
      order: true,
      limit: 100,
    },
  });

  const [challengeName, setChallengeName] = useState<string>('');
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const handleChallengeNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setChallengeName(e.target.value);
  };

  const handleStateChange = (value: string) => {
    setSelectedState(value);
  };

  // Filter data based on challenge name and state
  const filteredData = (data?.getApplicationByRegistration || []).filter(
    (item) => {
      const matchesName = item.challenge
        .toLowerCase()
        .includes(challengeName.toLowerCase());
      const matchesState = selectedState
        ? item.registration === selectedState
        : true;
      return matchesName && matchesState;
    }
  );

  const renderStatus = (state: string) => {
    switch (state) {
      case 'ACTIVE':
        return <div>{t('registration_open')}</div>;
      case 'ENDED':
        return <div>{t('registration_closed')}</div>;

      default:
        break;
    }
  };

  const columns = [
    {
      title: t('no'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (_, __, index) => {
        const actualIndex = (currentPage - 1) * 10 + index + 1;
        return `${actualIndex}.`;
      },
    },
    {
      title: t('challenge_name'),
      dataIndex: 'challenge',
      key: 'challenge',
      width: 300,
      render: (_, { id, challenge }) => (
        <Link href={`/intrapreneurship/challenge/${id}`}>{challenge}</Link>
      ),
    },
    {
      title: t('status'),
      dataIndex: 'registration',
      key: 'registration',
      render: (record) => <div>{renderStatus(record)}</div>,
    },
    {
      title: t('number_of_ideas'),
      dataIndex: 'applications',
      key: 'applications',
    },
  ];

  return (
    <div className={`${className}`}>
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <Input
            placeholder={t('search_challenge_name')}
            className="!w-64"
            value={challengeName}
            onChange={handleChallengeNameChange}
          />
          <Select
            style={{ width: 180 }}
            placeholder={t('by_challenge_state')}
            options={stateOptions}
            onChange={handleStateChange}
          />
        </div>
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={filteredData}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            current: currentPage,
            onChange: (page) => setCurrentPage(page),
            total: filteredData.length,
          }}
        />
      </div>
    </div>
  );
}

export default IntraChallengeIdeasStats;
